import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, BadgeCheck, Trash2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const Bar = ({ value, total }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
      <div className="h-full bg-[#D9923B]" style={{ width: `${pct}%` }} />
    </div>
  );
};

const ListingReviews = ({ listing }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ rating_avg: 0, rating_count: 0, distribution: {1:0,2:0,3:0,4:0,5:0} });
  const [myReview, setMyReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const isOwner = user?.user_id === listing.owner_id;
  const sellerId = listing.owner_id;
  const listingId = listing.listing_id;

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/listings/${listingId}/reviews`),
        api.get(`/users/${sellerId}/rating-summary`),
      ]);
      setReviews(a.data.reviews || []);
      setSummary(b.data);
      if (user) {
        const mine = (a.data.reviews || []).find((r) => r.reviewer_id === user.user_id);
        if (mine) {
          setMyReview(mine);
          setRating(mine.rating);
          setComment(mine.comment);
        }
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listingId, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Log in to leave a review"); return; }
    if (rating < 1 || rating > 5) { toast.error("Pick a star rating"); return; }
    if (comment.trim().length < 1) { toast.error("Add a short comment"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { listing_id: listingId, rating, comment: comment.trim() });
      setMyReview(data.review);
      toast.success(myReview ? "Review updated" : "Review posted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review");
    } finally { setSubmitting(false); }
  };

  const deleteReview = async (review) => {
    if (!window.confirm("Delete this review?")) return;
    setBusyId(review.review_id);
    try {
      await api.delete(`/reviews/${review.review_id}`);
      toast.success("Review removed");
      if (review.reviewer_id === user?.user_id) {
        setMyReview(null);
        setRating(0);
        setComment("");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally { setBusyId(null); }
  };

  return (
    <section className="" data-testid="listing-reviews">
      {/* Summary banner — full-width hero strip with rating + distribution.
          Replaces the old narrow 2-col grid that left half the row empty. */}
      <div className="rounded-2xl bg-white border border-black/10 shadow-sm p-6 lg:p-8 mb-8">
        <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-center">
          <div className="text-center lg:text-left lg:border-r lg:border-black/10 lg:pr-8">
            <div className="label-overline text-[#C05A3C] mb-2">Seller reviews</div>
            <h2 className="font-display text-2xl sm:text-3xl mb-3">What buyers say</h2>
            <div className="flex items-center gap-3 justify-center lg:justify-start">
              <span className="font-display text-5xl leading-none text-[#1A2024]" data-testid="reviews-avg-rating">
                {summary.rating_avg ? summary.rating_avg.toFixed(1) : "0.0"}
              </span>
              <div>
                <StarRating value={summary.rating_avg} size={18} />
                <div className="text-xs text-muted-foreground mt-1">
                  {summary.rating_count} {summary.rating_count === 1 ? "review" : "reviews"}
                </div>
              </div>
            </div>
          </div>
          {/* Distribution — fills the right column edge-to-edge. */}
          <div className="space-y-2" data-testid="rating-distribution">
            {summary.rating_count > 0 ? (
              [5, 4, 3, 2, 1].map((s) => (
                <div key={s} className="flex items-center gap-3 text-xs">
                  <span className="w-10 text-muted-foreground font-medium">{s} star</span>
                  <Bar value={summary.distribution?.[s] || 0} total={summary.rating_count} />
                  <span className="w-10 text-right text-muted-foreground tabular-nums">{summary.distribution?.[s] || 0}</span>
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground italic">Be the first to leave a review for this seller.</div>
            )}
          </div>
        </div>
      </div>

      {/* Review form */}
      {user && !isOwner && (
        <form onSubmit={submit} className="bg-white border border-black/10 rounded-xl p-5 mb-8 max-w-3xl" data-testid="review-form">
          <div className="text-sm font-semibold mb-1">{myReview ? "Update your review" : "Leave a review"}</div>
          <div className="text-xs text-muted-foreground mb-3">Your name and photo will be visible. Be honest, be kind.</div>
          <div className="flex items-center gap-3 mb-3">
            <StarRating value={rating} onChange={setRating} size={26} testIdPrefix="review-star" />
            {rating > 0 && <span className="text-sm font-medium text-[#D9923B]">{rating} / 5</span>}
          </div>
          <Textarea
            data-testid="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share details about quality, delivery, communication…"
            rows={4}
            maxLength={2000}
          />
          <div className="text-[10px] text-muted-foreground mt-1">{comment.length}/2000</div>
          <div className="mt-3 flex items-center gap-2">
            <Button type="submit" disabled={submitting || !rating || !comment.trim()} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md" data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (myReview ? "Update review" : "Post review")}
            </Button>
            {myReview && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => deleteReview(myReview)}
                className="text-muted-foreground"
                disabled={busyId === myReview.review_id}
                data-testid="delete-my-review"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete my review
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Reviews list — 2-col masonry on wide screens so the area doesn't look
          empty when there are only a handful of reviews. The empty state is
          handled inside the summary banner above, so we render nothing here
          when reviews.length === 0. */}
      {reviews.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <div key={r.review_id} className="bg-white border border-black/10 rounded-xl p-5" data-testid={`review-${r.review_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={r.reviewer_picture || undefined} />
                  <AvatarFallback className="bg-[#233348] text-white text-sm">{r.reviewer_name?.slice(0, 1)?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.reviewer_name || "Buyer"}</span>
                    {r.verified_buyer && (
                      <Badge className="bg-[#4A5D23]/15 text-[#4A5D23] text-[10px]" title="Made a paid purchase">
                        <BadgeCheck className="w-3 h-3 mr-0.5" /> Verified buyer
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">{formatWhen(r.created_at)}</span>
                  </div>
                  <div className="mt-1"><StarRating value={r.rating} size={13} /></div>
                  <p className="text-sm mt-2 leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                  {(user?.role === "admin" || r.reviewer_id === user?.user_id) && (
                    <button
                      type="button"
                      onClick={() => deleteReview(r)}
                      className="text-xs text-muted-foreground hover:text-[#C05A3C] underline mt-2 inline-flex items-center gap-1"
                      disabled={busyId === r.review_id}
                      data-testid={`delete-review-${r.review_id}`}
                    >
                      {busyId === r.review_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                      {user?.role === "admin" ? "Remove (admin)" : "Delete"}
                    </button>
                  )}

                  {/* Seller reply — Airbnb-style nested response */}
                  <SellerReplyBlock review={r} isOwner={isOwner} onChanged={load} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/** Single review's seller-reply zone. Shows the published reply (if any) and
 *  exposes a form to the seller (owner) so they can post/edit/delete it. */
const SellerReplyBlock = ({ review, isOwner, onChanged }) => {
  const [draft, setDraft] = useState(review.seller_reply || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (draft.trim().length < 2) { toast.error("Reply is too short"); return; }
    setBusy(true);
    try {
      await api.post(`/reviews/${review.review_id}/reply`, { reply: draft.trim() });
      toast.success("Reply posted");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post reply");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/reviews/${review.review_id}/reply`);
      toast.success("Reply removed");
      setDraft("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove reply");
    } finally { setBusy(false); }
  };

  // Existing reply — render in nested card.
  if (review.seller_reply && !editing) {
    return (
      <div className="mt-3 ml-2 border-l-2 border-[#C05A3C]/30 pl-3" data-testid={`reply-${review.review_id}`}>
        <div className="text-[11px] uppercase tracking-wider text-[#C05A3C] font-semibold mb-1 inline-flex items-center gap-1">
          <BadgeCheck className="w-3 h-3" /> Reply from seller
          <span className="text-muted-foreground font-normal normal-case tracking-normal ml-2">{formatWhen(review.seller_reply_at)}</span>
        </div>
        <p className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{review.seller_reply}</p>
        {isOwner && (
          <div className="flex items-center gap-3 mt-1">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs text-muted-foreground hover:text-[#C05A3C] underline"
              data-testid={`edit-reply-${review.review_id}`}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-xs text-muted-foreground hover:text-[#C05A3C] underline inline-flex items-center gap-1"
              data-testid={`delete-reply-${review.review_id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  // Composer — only for the seller (listing owner), and only when no reply or editing.
  if (!isOwner) return null;

  return (
    <div className="mt-3 ml-2 border-l-2 border-[#C05A3C]/20 pl-3" data-testid={`reply-form-${review.review_id}`}>
      {!editing && !review.seller_reply ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-[#C05A3C] hover:underline font-semibold inline-flex items-center gap-1"
          data-testid={`open-reply-${review.review_id}`}
        >
          <MessageSquareText className="w-3 h-3" /> Reply as seller
        </button>
      ) : (
        <div>
          <Textarea
            data-testid={`reply-input-${review.review_id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="A short, warm reply — say thanks, clarify, or share context."
            className="text-sm"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              data-testid={`reply-save-${review.review_id}`}
              size="sm"
              onClick={save}
              disabled={busy || draft.trim().length < 2}
              className="bg-[#C05A3C] hover:bg-[#A8492F] text-white h-8 text-xs"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {review.seller_reply ? "Update reply" : "Post reply"}
            </Button>
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft(review.seller_reply || ""); }}
              className="text-xs text-muted-foreground hover:text-[#1A1A1A]"
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingReviews;
